// src/components/AdminPanel/AdminPharmacyTable.js
import React from "react";
import { Table, Tag, Button, Modal, Descriptions, message, Alert} from "antd";
import { updatePharmacyStatus } from "../../api/pharmacy";
import { updatePharmacyDetails } from "../../api/pharmacy"; // Import the API function for updating pharmacy details
import AdminPharmacyEditor from "./AdminPharmacyEditor";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const AdminPharmacyTable = ({ data, status, onStatusUpdated }) => {
  const [selected, setSelected] = React.useState(null);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editModalOpen, setEditModalOpen] = React.useState(false);
  const [successBanner, setSuccessBanner] = React.useState(false);

  const handleStatusChange = async (id, newStatus) => {
    const res = await updatePharmacyStatus(id, newStatus);
    if (res?.message) {
      message.success(`Pharmacy ${newStatus}`);
      if (typeof onStatusUpdated === "function") {
        onStatusUpdated();
      }
    } else {
      message.error("Failed to update status");
    }
  };

  const handleEditSubmit = async (updatedData) => {
    const res = await updatePharmacyDetails(updatedData);
    if (res?.success) {
      message.success("Pharmacy updated");
      setEditModalOpen(false);
      setSuccessBanner(true); // Show banner
      onStatusUpdated();
      setTimeout(() => setSuccessBanner(false), 4000); // Hide after 4s
    } else {
      message.error("Update failed");
    }
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Pincode", dataIndex: "pincode", key: "pincode" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag
          color={
            status === "approved"
              ? "green"
              : status === "rejected"
              ? "red"
              : "orange"
          }
        >
          {status}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <>
          <Button
            type="link"
            onClick={() => {
              setSelected(record);
              setModalOpen(true);
            }}
          >
            Show Details
          </Button>
          <Button
            onClick={() => {
              setSelected(record);
              setEditModalOpen(true);
            }}
            size="small"
          >
            Edit
          </Button>{" "}
          <Button
            onClick={() => handleStatusChange(record.id, "approved")}
            type="primary"
            size="small"
          >
            Approve
          </Button>{" "}
          <Button
            onClick={() => handleStatusChange(record.id, "rejected")}
            danger
            size="small"
          >
            Reject
          </Button>{" "}
          <Button
            onClick={() => handleStatusChange(record.id, "pending")}
            size="small"
          >
            Hold
          </Button>
        </>
      ),
    },
    {
      title: "Verified",
      dataIndex: "isVerified",
      key: "isVerified",
      render: (verified) =>
        verified ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>,
    },
  ];

  return (
    <div style={{ padding: "16px", flex: 1 }}>
      <h2>{status.charAt(0).toUpperCase() + status.slice(1)} Registrations</h2>
      {successBanner && (
        <Alert
          message="Pharmacy details updated successfully"
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      <Table dataSource={data} columns={columns} rowKey="id" />

      <Modal
        open={modalOpen}
        title="Pharmacy Details"
        onCancel={() => setModalOpen(false)}
        footer={null}
      >
        {selected && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="Name">{selected.name}</Descriptions.Item>
            <Descriptions.Item label="License Number">
              {selected.license_number}
            </Descriptions.Item>
            <Descriptions.Item label="Phone">
              {selected.phone_country_code} {selected.phone_number}
            </Descriptions.Item>
            <Descriptions.Item label="WhatsApp">
              {selected.whatsapp_country_code} {selected.whatsapp_number}
            </Descriptions.Item>
            <Descriptions.Item label="Pincode">
              {selected.pincode}
            </Descriptions.Item>
            <Descriptions.Item label="Services">
              {selected.services}
            </Descriptions.Item>
            <Descriptions.Item label="Order Methods">
              {selected.order_methods}
            </Descriptions.Item>
            <Descriptions.Item label="Address">
              {selected.address_line1}, {selected.locality},{" "}
              {selected.city_town}, {selected.district}, {selected.state}
            </Descriptions.Item>
            <Descriptions.Item label="Google Maps">
              <a
                href={selected.google_maps_link}
                target="_blank"
                rel="noreferrer"
              >
                View on Map
              </a>
            </Descriptions.Item>
            <Descriptions.Item label="Open 24 Hours">
              {selected.is_open_24_hours ? (
                <Tag color="green">Yes</Tag>
              ) : (
                <Tag color="volcano">No</Tag>
              )}
            </Descriptions.Item>
            {!selected.is_open_24_hours && (
              <>
                <Descriptions.Item label="Open From">
                  {selected.open_from || "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Open To">
                  {selected.open_to || "N/A"}
                </Descriptions.Item>
              </>
            )}
            <Descriptions.Item label="License Doc">
              <a
                href={`${BASE_URL}/uploads/${selected.license_doc}`}
                target="_blank"
                rel="noreferrer"
              >
                View Document
              </a>
            </Descriptions.Item>
            <Descriptions.Item label="Pharmacy Images">
              {selected.pharmacy_images
                ? selected.pharmacy_images.split(",").map((img, idx) => (
                    <div key={idx}>
                      <a
                        href={`${BASE_URL}/uploads/${img}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Image {idx + 1}
                      </a>
                    </div>
                  ))
                : "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Is Verified">
              {selected.isVerified ? (
                <Tag color="green">Yes</Tag>
              ) : (
                <Tag color="volcano">No</Tag>
              )}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <Modal
        open={editModalOpen}
        title="Edit Pharmacy Details"
        onCancel={() => setEditModalOpen(false)}
        footer={null}
      >
        {selected && (
          <AdminPharmacyEditor
            open={editModalOpen}
            onCancel={() => setEditModalOpen(false)}
            data={selected}
            onSubmit={handleEditSubmit}
          />
        )}
      </Modal>
    </div>
  );
};

export default AdminPharmacyTable;
